import type { JSX } from "react"
import { addHours, differenceInMinutes, isAfter } from "date-fns"

import type { Feedback, LiveChatSession } from "@productlane/zero"

import { useTimeTick } from "@/lib/hooks/use-time-tick"
import { computeSlaDeadline, formatTimeRemaining } from "@/lib/sla-utils"
import { useZeroWorkspace } from "@/lib/zero"
import { ResponseBadges } from "./response-badges"

export const isWaitingOnCustomer = (
	lastInbound: string | number | null,
	lastOutbound: string | number | null,
): boolean => {
	if (!lastInbound && !lastOutbound) return false
	if (!lastInbound) return true // Only outbound exists → waiting on customer
	if (!lastOutbound) return false // Only inbound exists → we owe a reply

	return new Date(lastOutbound) > new Date(lastInbound)
}

export const isWaitingOnReply = (
	lastInbound: string | number | null,
	lastOutbound: string | number | null,
): boolean => {
	if (!lastInbound && !lastOutbound) return true
	if (!lastOutbound) return true // Only inbound exists → we owe a reply
	if (!lastInbound) return true // Only outbound exists → already responded

	return new Date(lastInbound) > new Date(lastOutbound)
}

type FeedbackWithLiveChatSession = Feedback & {
	liveChatSession?: LiveChatSession
}

export const useResponseBadge = (
	thread: FeedbackWithLiveChatSession | undefined,
): {
	showWaitingClock: boolean
	extraBadges?: JSX.Element
} | null => {
	const workspace = useZeroWorkspace()
	// Re-renders every 60s so SLA status stays current
	useTimeTick()

	// Thread must be defined
	if (!thread) return null

	// Priority-aware SLA check — runs before live chat early return so breach/atRisk always shows
	if (workspace?.slaEnabled) {
		const slaInfo = computeSlaDeadline(thread)
		const timeText = slaInfo ? formatTimeRemaining(slaInfo.deadline) : null

		if (slaInfo?.status === "breached") {
			return {
				showWaitingClock: false,
				extraBadges: (
					<ResponseBadges.SlaBreached timeText={timeText ?? undefined} />
				),
			}
		}
	}

	if (
		thread.origin === "LIVE_CHAT" &&
		thread.liveChatSession &&
		!thread.liveChatSession.isFinished
	) {
		return {
			showWaitingClock: false,
		}
	}

	const lastInboundMessageAt = new Date(
		thread.lastInboundMessageAt || thread.createdAt,
	)

	if (thread.state === "UNSNOOZED") {
		// Show unsnoozed badge when thread was unsnoozed
		const hasReplied =
			thread.lastOutboundMessageAt &&
			isAfter(new Date(thread.lastOutboundMessageAt), lastInboundMessageAt)

		let shouldShowWarning = false

		if (workspace?.slaEnabled) {
			const slaInfo = computeSlaDeadline(thread)
			shouldShowWarning =
				slaInfo?.status === "breached" ||
				slaInfo?.status === "highRisk" ||
				slaInfo?.status === "mediumRisk"
		} else {
			shouldShowWarning = Boolean(
				!hasReplied &&
				workspace?.maxResponseTimeHours &&
				differenceInMinutes(
					new Date(),
					addHours(lastInboundMessageAt, workspace.maxResponseTimeHours),
				) >= 0,
			)
		}

		return {
			showWaitingClock: shouldShowWarning,
			extraBadges: <ResponseBadges.Unsnoozed />,
		}
	}

	if (thread.origin === "INAPP" && !thread.contactId) {
		return {
			showWaitingClock: false,
		}
	}

	// Show badge when reply was sent
	if (
		thread.lastOutboundMessageAt &&
		isAfter(new Date(thread.lastOutboundMessageAt), lastInboundMessageAt)
	) {
		return {
			showWaitingClock: false,
		}
	}

	// Fallback to legacy maxResponseTimeHours
	if (workspace?.maxResponseTimeHours) {
		const dateToRespond = addHours(
			lastInboundMessageAt,
			workspace.maxResponseTimeHours,
		)

		const responseTimeLeft = differenceInMinutes(new Date(), dateToRespond)
		const hasReplied =
			thread.lastOutboundMessageAt &&
			isAfter(new Date(thread.lastOutboundMessageAt), lastInboundMessageAt)

		if (thread.state === "NEW") {
			if (responseTimeLeft < 0) {
				return {
					showWaitingClock: false,
				}
			} else if (!hasReplied) {
				return {
					showWaitingClock: true,
				}
			}
		}
	}

	// created at badge when there is no badges
	return {
		showWaitingClock: false,
	}
}
